import React, { useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../services/apiClient';
import { syncRememberMeAuthFromSession } from '../../utils/rememberMe';

/**
 * Modal shown when JWT session is about to expire (15 minutes or less)
 * Allows user to stay signed in by refreshing the token
 */
const SessionTimeoutWarningModal = ({ 
  isOpen, 
  remainingTime, 
  onStaySignedIn, 
  onLogout,
  continueMessage = 'To complete your payment securely, we need to refresh your session. Click "Stay Signed In" to continue, or you\'ll be logged out.',
  successToastMessage = 'Session extended. You can continue with your payment.'
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!isOpen) return null;

  const handleStaySignedIn = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 [SessionTimeoutWarningModal] Refreshing token...');
      
      // Call refresh token endpoint
      const response = await apiClient.post('/auth/refresh-token');
      
      if (response.success && response.data?.token) {
        console.log('✅ [SessionTimeoutWarningModal] Token refreshed successfully');
        
        // Store the new token in sessionStorage
        sessionStorage.setItem('token', response.data.token);
        syncRememberMeAuthFromSession();
        console.log('💾 [SessionTimeoutWarningModal] New token stored in sessionStorage');
        
        toast.success(successToastMessage);
        
        // Set refreshing to false to hide spinner
        setIsRefreshing(false);
        
        // Call the callback to proceed with payment
        if (onStaySignedIn) {
          onStaySignedIn();
        }
      } else {
        console.error('❌ [SessionTimeoutWarningModal] Token refresh failed:', response.message);
        toast.error('Failed to extend session. Please login again.');
        setIsRefreshing(false);
        if (onLogout) {
          onLogout();
        }
      }
    } catch (error) {
      console.error('❌ [SessionTimeoutWarningModal] Error refreshing token:', error);
      console.error('❌ [SessionTimeoutWarningModal] Error details:', {
        message: error.message,
        status: error.status,
        data: error.data
      });
      toast.error('Failed to extend session. Please login again.');
      setIsRefreshing(false);
      if (onLogout) {
        onLogout();
      }
    }
  };

  const handleLogout = () => {
    console.log('🚪 [SessionTimeoutWarningModal] User chose to logout');
    
    // Use existing logout utility
    import('../../utils/logout').then(({ performSimpleLogout }) => {
      performSimpleLogout();
    });
  };

  // Format remaining time
  const formatRemainingTime = () => {
    if (!remainingTime) return '0 minutes';
    
    const minutes = Math.floor(remainingTime.remaining_time.minutes);
    const seconds = remainingTime.remaining_time.seconds;
    
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95">
        {isRefreshing ? (
          // Loading State
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-6">
              <div className="w-16 h-16 rounded-full border-4 border-amber-200 border-t-amber-600 animate-spin"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Extending Your Session</h3>
            <p className="text-sm text-gray-600 text-center">
              Please wait while we refresh your session...
            </p>
          </div>
        ) : (
          // Normal State
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Session Expiring Soon</h3>
                <p className="text-sm text-gray-500">Your session will expire in {formatRemainingTime()}</p>
              </div>
            </div>

            {/* Message */}
            <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-700">
                {continueMessage}
              </p>
            </div>

            {/* Remaining Time Display */}
            <div className="mb-6 p-3 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-600 mb-1">Time Remaining</p>
              <p className="text-2xl font-bold text-amber-600">
                {String(remainingTime?.remaining_time?.minutes || 0).padStart(2, '0')}:
                {String(remainingTime?.remaining_time?.seconds || 0).padStart(2, '0')}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleLogout}
                disabled={isRefreshing}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Logout
              </button>
              <button
                onClick={handleStaySignedIn}
                disabled={isRefreshing}
                className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                Stay Signed In
              </button>
            </div>

            {/* Footer Note */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Your session will be extended for another 24 hours
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SessionTimeoutWarningModal;
